/**
 * OffAirTitle
 * ─────────────────────────────────────────────────────────────
 * Bookend of HeroName: same per-letter inverse parallax, run in reverse.
 *
 * HeroName (top of the page):
 *   scroll down → letters shoot UP and fan apart
 *
 * OffAirTitle (bottom of the page):
 *   scroll into the sign-off → letters rise UP into place
 *   scroll back up → they drop DOWN and fan apart again
 *
 * Progress comes from the #offair runway (same 0→1 measure OffAir uses),
 * not raw page scrollY — the stage is fixed, so document scroll alone
 * wouldn't know when the title is actually on screen.
 *
 *   remaining = 1 − eased(runway progress in the assemble window)
 *   letter Y  = +remaining × CHAR_LEAD_FACTORS[i] × maxLead
 *
 * Positive Y = below rest. At the end of the page remaining is 0 and the
 * wordmark sits still, just like the hero at scroll=0.
 *
 * Public API:
 *   destroy()
 */

// Per-letter lead factor. 17 entries = "Thanks for" (9) + "tuning in" (8).
// Same hand-tuned spread as the hero: slow ~0.20, rocket ~0.75, no pattern.
const CHAR_LEAD_FACTORS = [
  // T      h      a      n      k      s           f      o      r
  0.35, 0.60, 0.25, 0.70, 0.40, 0.55,        0.30, 0.72, 0.22,
  // t      u      n      i      n      g           i      n
  0.48, 0.68, 0.28, 0.58, 0.42, 0.75,        0.33, 0.50,
];

// Ceiling on per-letter travel. Letters rise from below rest; 360px is
// enough to start them off the bottom edge and stagger in at different
// speeds without overshooting the overflow:hidden stage.
const CHAR_LEAD_MAX_PX = 360;

// Runway window. Off-air copy used to fade in around 0.72; the title
// has to stay visible earlier so you actually see the letters arrive
// (and depart again on the way back up).
const ASSEMBLE_START = 0.52;
const ASSEMBLE_END = 0.96;
const FADE_START = 0.46;
const FADE_END = 0.56;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export class OffAirTitle {
  constructor({
    root = document.querySelector('.offair__title'),
    section = document.getElementById('offair'),
  } = {}) {
    if (!root || !section) return;
    this.root = root;
    this.section = section;
    this._chars = [];
    this._p = 0;

    this._split();
    if (prefersReducedMotion()) {
      this.root.style.opacity = '1';
      return;
    }

    this._raf = requestAnimationFrame(this._tick);
  }

  // ── boot ────────────────────────────────────────────────────

  /**
   * Split each .offair__title-line into one span per visible character.
   * Spaces stay as non-animated spacers so the two-line wordmark keeps
   * its word breaks. Same a11y treatment as HeroName: hide the visual
   * line from AT and keep a visually-hidden sibling with the original text.
   */
  _split() {
    const lines = this.root.querySelectorAll('.offair__title-line');

    lines.forEach((line) => {
      const text = line.textContent;

      if (line.getAttribute('aria-hidden') !== 'true') {
        line.setAttribute('aria-hidden', 'true');
        const prev = line.previousElementSibling;
        const alreadyHasA11ySibling =
          prev?.classList.contains('visually-hidden') &&
          prev?.textContent.trim() === text.trim();
        if (!alreadyHasA11ySibling) {
          const sr = document.createElement('span');
          sr.className = 'visually-hidden';
          sr.textContent = text;
          line.parentNode.insertBefore(sr, line);
        }
      }

      line.textContent = '';
      for (const ch of text) {
        if (ch === ' ') {
          const space = document.createElement('span');
          space.className = 'offair__title-space';
          space.innerHTML = '&nbsp;';
          line.appendChild(space);
          continue;
        }
        const span = document.createElement('span');
        span.className = 'offair__title-char';
        span.textContent = ch;
        span.style.transform = 'translate3d(0, 0, 0)';
        line.appendChild(span);
        this._chars.push(span);
      }
    });
  }

  // ── measure ─────────────────────────────────────────────────

  /** Same runway progress OffAir uses: 0 at section-top/viewport-bottom, 1 at end. */
  _measure() {
    const r = this.section.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    return Math.max(0, Math.min(1, (vh - r.top) / Math.max(1, r.height)));
  }

  // ── render tick ─────────────────────────────────────────────

  _tick = () => {
    this._raf = requestAnimationFrame(this._tick);
    const p = this._measure();
    this._p = p;

    const fade = clamp((p - FADE_START) / (FADE_END - FADE_START), 0, 1);
    this.root.style.opacity = fade.toFixed(3);

    const t = clamp((p - ASSEMBLE_START) / (ASSEMBLE_END - ASSEMBLE_START), 0, 1);
    // remaining travel: 1 = fully scattered below, 0 = landed.
    const remaining = 1 - t;
    const max = Math.min(CHAR_LEAD_MAX_PX, (window.innerHeight || CHAR_LEAD_MAX_PX) * 0.48);

    for (let i = 0; i < this._chars.length; i++) {
      const factor = CHAR_LEAD_FACTORS[i] ?? 0.40;
      // Positive = below rest — letters stagger upward into place.
      const offset = remaining * factor * max;
      this._chars[i].style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    }
  };

  // ── teardown ────────────────────────────────────────────────

  destroy() {
    if (this._raf) cancelAnimationFrame(this._raf);
  }
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}
